import { Injectable } from '@angular/core'
import { createStore, withProps } from '@ngneat/elf'
import { map, Observable, switchMap, tap } from 'rxjs'
import {
  ActiveOrderGQL,
  OrderByIdGQL,
  ActiveOrderResult,
  AddItemToOrderGQL,
  AdjustOrderLineGQL,
  Order,
  ProductVariant,
  RemoveAllOrderLinesGQL,
  RemoveOrderLineGQL,
} from '../gql/generated'
import { LoaderService } from './loader.service'

export interface CartState {
  activeOrder: ActiveOrderResult | null
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  cartStore$ = createStore(
    { name: 'cart' },
    withProps<CartState>({ activeOrder: null })
  )

  constructor(
    private activeOrderGQL: ActiveOrderGQL,
    private orderById: OrderByIdGQL,
    private addItemToOrderGQL: AddItemToOrderGQL,
    private removeAllOrderLinesGQL: RemoveAllOrderLinesGQL,
    private removeOrderLineGQL: RemoveOrderLineGQL,
    private adjustOrderLineGQL: AdjustOrderLineGQL,
    private loaderService: LoaderService
  ) {
    this.fetchCartItems().subscribe()
  }

  fetchCartItems() {
    return this.loaderService.withLoader(
      () =>
        this.activeOrderGQL
          .fetch(
            undefined, 
            {
              context: {
                headers: {
                  'vendure-token': 'K7',
                },
              },
              fetchPolicy: 'network-only',
            }
          ).pipe(
            tap({
              next: (response) => {
                this.cartStore$.update((state) => ({
                  ...state,
                  activeOrder: response.data.activeOrder as ActiveOrderResult,
                }))
              },
            })
          ),
      CartService.name
    )
  }
  
  fetchOrderById(id: string) {
    return this.loaderService.withLoader(
      () =>
        this.orderById
          .fetch(
            { id },
            {
              context: {
                headers: {
                  'vendure-token': 'K7',
                },
              },
              fetchPolicy: 'network-only',
            }
          ).pipe(
            tap({
              next: (response) => {
                console.log(response.data.order);
              },
            })
          ),
      CartService.name
    )
  }

  addToCart(productVariant: ProductVariant) {
    return this.loaderService.withLoader(
      () =>
        this.addItemToOrderGQL
          .mutate(
            {
              productVariantId: productVariant.id.toString(),
              quantity: 1,
            },
            {
              context: {
                headers: {
                  'vendure-token': 'K7',
                },
              }
            }
          )
          .pipe(switchMap(() => this.fetchCartItems())),
      CartService.name
    )
  }

  adjustCartQuantity(orderLineId: string, quantity: number) {
    return this.loaderService.withLoader(
      () =>
        this.adjustOrderLineGQL
          .mutate(
            {
              orderLineId,
              quantity,
            },
            {
              context: {
                headers: {
                  'vendure-token': 'K7',
                },
              }
            }
          )
          .pipe(switchMap(() => this.fetchCartItems())),
      CartService.name
    )
  }

  removeFromCart(orderLineId: string) {
    return this.loaderService.withLoader(
      () =>
        this.removeOrderLineGQL
          .mutate(
            { orderLineId },
            {
              context: {
                headers: {
                  'vendure-token': 'K7',
                },
              }
            }
          )
          .pipe(switchMap(() => this.fetchCartItems())),
      CartService.name
    )
  }

  clearCart() {
    return this.loaderService.withLoader(
      () =>
        this.removeAllOrderLinesGQL
          .mutate(
            undefined,
            {
              context: {
                headers: {
                  'vendure-token': 'K7',
                },
              }
            }
          )
          .pipe(switchMap(() => this.fetchCartItems())),
      CartService.name
    )
  }

  getCartState(): CartState {
    return this.cartStore$.getValue()
  }

  getCartItemCount(): Observable<number> {
    return this.loaderService.withLoader(
      () =>
        this.cartStore$.pipe(
          map(
            (state) =>
              (state.activeOrder as Order)?.lines.reduce(
                (acc, line) => acc + line.quantity,
                0
              ) || 0
          )
        ),
      CartService.name
    )
  }
}
